import { useCallback, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import googleSheetsIntegrationApi from '../../../api/googleSheetsIntegrationApi';
import { toast } from 'react-hot-toast';
import { getUserFriendlyErrorMessage } from '../../../utils/errorMessages';
/** Prefer FastAPI `detail` (e.g. 404 "Not Found") over generic axios message. */
function axiosErrorDetail(err: unknown): string {
    if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as { response?: { data?: { detail?: unknown } } }).response?.data;
        const d = data?.detail;
        if (typeof d === 'string' && d.trim()) return d;
        if (Array.isArray(d)) {
            const parts = d.map((x) => (typeof x === 'object' && x && 'msg' in x ? String((x as { msg: string }).msg) : String(x)));
            if (parts.length) return parts.join('; ');
        }
    }
    if (err instanceof Error && err.message) return getUserFriendlyErrorMessage(err);
    return '';
}

function toastSheetsApiError(e: unknown, fallback: string) {
    const detail = axiosErrorDetail(e);
    if (detail === 'Google OAuth is not configured') {
        toast.error(
            'Google OAuth is not configured on the API. In finz-backend-v2/.env set GOOGLE_OAUTH_CLIENT_ID, GOOGLE_OAUTH_CLIENT_SECRET, and GOOGLE_OAUTH_REDIRECT_URI (see .env.example), add the same redirect URI in Google Cloud Console, then restart the FastAPI container.',
            { duration: 10000 },
        );
        return;
    }
    if (detail === 'Google Sheets ingestion is disabled') {
        toast.error(
            'Google Sheets ingestion is off. Set GOOGLE_SHEETS_INGESTION_ENABLED=true in finz-backend-v2/.env and restart FastAPI + Kafka consumer.',
            { duration: 10000 },
        );
        return;
    }
    toast.error(getUserFriendlyErrorMessage(e, detail || fallback));
}

/** Minimal typings for google.picker (loaded from https://apis.google.com/js/api.js). */
type GooglePickerBuilder = {
    addView: (v: unknown) => GooglePickerBuilder;
    enableFeature: (f: unknown) => GooglePickerBuilder;
    setOAuthToken: (t: string) => GooglePickerBuilder;
    setDeveloperKey: (k: string) => GooglePickerBuilder;
    setCallback: (cb: (data: Record<string, unknown>) => void) => GooglePickerBuilder;
    build: () => { setVisible: (v: boolean) => void };
};

type GooglePickerApi = {
    PickerBuilder: new () => GooglePickerBuilder;
    ViewId: { SPREADSHEETS: unknown };
    Feature: { MULTISELECT_ENABLED: unknown };
    Action: { PICKED: string };
    Response: { ACTION: string; DOCUMENTS: string };
};

type GapiWindow = Window & {
    gapi?: { load: (api: string, cb: () => void) => void };
    google?: { picker?: GooglePickerApi };
};

function getPickerApi(): GooglePickerApi {
    const w = window as GapiWindow;
    const picker = w.google?.picker;
    if (!picker) {
        throw new Error('Google Picker API not loaded');
    }
    return picker;
}

function loadPickerApi(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (typeof window === 'undefined') {
            reject(new Error('no window'));
            return;
        }
        const w = window as GapiWindow;
        if (w.gapi?.load) {
            w.gapi.load('picker', () => resolve());
            return;
        }
        const s = document.createElement('script');
        s.src = 'https://apis.google.com/js/api.js';
        s.async = true;
        s.onload = () => {
            (window as GapiWindow).gapi?.load('picker', () => resolve());
        };
        s.onerror = () => reject(new Error('Failed to load Google API'));
        document.body.appendChild(s);
    });
}

export function useGoogleSheetsIntegration(
    onRefresh: () => void,
    options?: { enabled?: boolean; onImportQueued?: () => void },
) {
    const enabled = options?.enabled !== false;
    const [status, setStatus] = useState<{ connected: boolean; email?: string } | null>(null);
    const [loading, setLoading] = useState(false);
    const pollTimers = useRef<ReturnType<typeof setInterval>[]>([]);
    const onRefreshRef = useRef(onRefresh);
    onRefreshRef.current = onRefresh;
    const onImportQueuedRef = useRef(options?.onImportQueued);
    onImportQueuedRef.current = options?.onImportQueued;

    const refreshStatus = useCallback(async () => {
        try {
            const s = await googleSheetsIntegrationApi.getStatus();
            setStatus({ connected: s.connected, email: s.provider_account_email });
        } catch {
            setStatus({ connected: false });
        }
    }, []);

    useEffect(() => {
        if (!enabled) return;
        void refreshStatus();
    }, [refreshStatus, enabled]);

    useEffect(() => {
        return () => {
            pollTimers.current.forEach(clearInterval);
        };
    }, []);

    const handleConnect = useCallback(async () => {
        try {
            const { auth_url } = await googleSheetsIntegrationApi.getConnectUrl();
            window.location.href = auth_url;
        } catch (e: unknown) {
            toastSheetsApiError(e, 'Could not start Google OAuth');
        }
    }, []);

    /** Re-fetch workspace + file list; call twice on import success to beat async UI/closure timing. */
    const bumpDocumentsList = useCallback(() => {
        onRefreshRef.current();
        window.setTimeout(() => {
            onRefreshRef.current();
        }, 450);
    }, []);

    const pollJob = useCallback(
        (jobId: string) => {
            // Count consecutive poll errors so a one-off network blip
            // doesn't kill the poll silently the way the old
            // ``catch { clearInterval }`` did. After ~5 failures in a
            // row we assume the job endpoint is genuinely unreachable
            // and surface a toast so the user doesn't stare at a
            // forever-spinner.
            let consecutiveErrors = 0;
            const t = setInterval(() => {
                void (async () => {
                    try {
                        const j = await googleSheetsIntegrationApi.getJob(jobId);
                        consecutiveErrors = 0;
                        if (j.status === 'completed' || j.status === 'failed') {
                            clearInterval(t);
                            pollTimers.current = pollTimers.current.filter((x) => x !== t);
                            if (j.status === 'completed') {
                                bumpDocumentsList();
                                // One short toast only — never mirror long worker/API copy here.
                                const action = j.finz_docs_row_action;
                                const label =
                                    action === 'updated' || action === 'restored'
                                        ? 'Sheet updated'
                                        : 'Sheet imported';
                                toast.success(label, { duration: 2000 });
                            } else {
                                toast.error(j.error || 'Import failed');
                                // Refresh so the DocCard flips from
                                // spinner → failed badge, not stuck on
                                // "ingesting" forever.
                                bumpDocumentsList();
                            }
                        } else if (j.status === 'running') {
                            // Periodically re-read the documents list
                            // so ``ingestion_status`` reflects any
                            // backend-side progress (failed tabs,
                            // etc.) without waiting for terminal
                            // status.
                            onRefreshRef.current();
                        }
                    } catch (err) {
                        consecutiveErrors += 1;
                        if (consecutiveErrors >= 5) {
                            clearInterval(t);
                            pollTimers.current = pollTimers.current.filter((x) => x !== t);
                            toast.error(
                                'Lost connection to the ingestion job. Refresh the page to re-check status.',
                                { duration: 6000 },
                            );
                            // Force a refetch so any terminal status
                            // that landed during the blip shows up.
                            onRefreshRef.current();
                        } else {
                            // eslint-disable-next-line no-console
                            console.warn('[google_sheets] poll attempt failed', err);
                        }
                    }
                })();
            }, 900);
            pollTimers.current.push(t);
        },
        [bumpDocumentsList],
    );

    const handleImport = useCallback(async () => {
        const devKey = process.env.REACT_APP_GOOGLE_PICKER_API_KEY;
        if (!devKey) {
            toast.error('Set REACT_APP_GOOGLE_PICKER_API_KEY (Google Cloud API key, Picker enabled)');
            return;
        }
        setLoading(true);
        try {
            await loadPickerApi();
            const { access_token } = await googleSheetsIntegrationApi.getAccessToken();
            const cid =
                typeof crypto !== 'undefined' && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `req_${Date.now()}_${Math.random().toString(36).slice(2)}`;

            const gp = getPickerApi();
            const picker = new gp.PickerBuilder()
                .addView(gp.ViewId.SPREADSHEETS)
                .enableFeature(gp.Feature.MULTISELECT_ENABLED)
                .setOAuthToken(access_token)
                .setDeveloperKey(devKey)
                .setCallback((data: Record<string, unknown>) => {
                    const action = data[gp.Response.ACTION] as string;
                    if (action === gp.Action.PICKED) {
                        const docs =
                            (data[gp.Response.DOCUMENTS] as { id?: string; name?: string }[]) || [];
                        const picked = docs.filter((d): d is { id: string; name?: string } =>
                            Boolean(d?.id),
                        );
                        const ids = picked.map((d) => d.id);
                        // Drive Picker gives us ``name`` for every selected
                        // file; pipe those through to the backend so the
                        // optimistic row renders the actual spreadsheet
                        // title while the worker runs (instead of a
                        // generic "Imported Google Sheet" placeholder).
                        const titles: Record<string, string> = {};
                        picked.forEach((d) => {
                            if (d.name && d.name.trim()) titles[d.id] = d.name.trim();
                        });
                        if (ids.length) {
                            void (async () => {
                                try {
                                    const imp = await googleSheetsIntegrationApi.importSheets(
                                        ids,
                                        cid,
                                        titles,
                                    );
                                    toast.success('Import started', { duration: 2200 });
                                    // Refetch IMMEDIATELY so the ingesting
                                    // placeholder rows (created by the
                                    // import endpoint) show up before the
                                    // worker finishes.
                                    onImportQueuedRef.current?.();
                                    bumpDocumentsList();
                                    imp.job_ids.forEach((jid) => pollJob(jid));
                                } catch (err: unknown) {
                                    toastSheetsApiError(err, 'Import failed');
                                }
                            })();
                        }
                    }
                })
                .build();
            picker.setVisible(true);
        } catch (e: unknown) {
            const detail = axiosErrorDetail(e);
            const status = isAxiosError(e) ? e.response?.status : undefined;
            const is404 = status === 404 || detail === 'Not Found';
            if (is404) {
                toast.error(
                    'Google Sheets API not found (404). Rebuild the FastAPI image so /integrations routes exist: from finz-backend-v2 run docker-compose build fastapi && docker-compose up -d fastapi (or ./start-local-dev.sh --rebuild).',
                );
            } else {
                toastSheetsApiError(e, 'Picker failed');
            }
        } finally {
            setLoading(false);
        }
    }, [pollJob, bumpDocumentsList]);

    const handleDisconnect = useCallback(async () => {
        setLoading(true);
        try {
            await googleSheetsIntegrationApi.disconnect();
            toast.success('Google account disconnected');
            await refreshStatus();
            onRefreshRef.current();
        } catch (e: unknown) {
            const msg =
                e && typeof e === 'object' && 'response' in e
                    ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail || '')
                    : '';
            toast.error(msg || (e instanceof Error ? e.message : 'Disconnect failed'));
        } finally {
            setLoading(false);
        }
    }, [refreshStatus]);

    return {
        status,
        loading,
        refreshStatus,
        handleConnect,
        handleImport,
        handleDisconnect,
    };
}
